import type { CSSProperties, ReactNode } from 'react';
import type { BlockField, BlockFieldStyle } from '@/blocks/domain/types';
import type { DocumentContext } from '@/document-render/domain/DocumentContext';
import { UNBOUND_TOKEN_CHIP_CSS } from '@/document-render/application/unboundTokenHtml';

const TEXT_STYLES: ReadonlySet<BlockFieldStyle> = new Set<BlockFieldStyle>([
  'title',
  'heading',
  'subheading',
  'value',
  'text',
  'label_value',
  'caption'
]);

const TOKEN_PATTERN = /\{\{\s*([\w.]+)\s*\}\}/g;

const TOKEN_CHIP_STYLE: CSSProperties = UNBOUND_TOKEN_CHIP_CSS;

/**
 * The editor canvas with no bound values (`previewValueBinding: 'unbound'`) shows the author's raw
 * value with each `{{token}}` drawn as a chip instead of an empty slot. Returns null when the value
 * holds no token, so plain authored text keeps its normal rendering.
 */
interface UnboundTokenOptions {
  /** Title rows show literal tokens without the orange chip background (LLAMS-610). */
  chip?: boolean;
}

function tokenSpan(token: string, index: number, chip: boolean): ReactNode {
  const style = chip ? TOKEN_CHIP_STYLE : undefined;
  return (
    <span key={`token-${index}`} data-variable-token={token} style={style}>
      {`{{${token}}}`}
    </span>
  );
}

export function renderUnboundTokens(rawValue: string | null | undefined, options: UnboundTokenOptions = {}): ReactNode | null {
  const chip = options.chip !== false;
  const raw = rawValue?.trim() ?? '';
  const matches = [...raw.matchAll(TOKEN_PATTERN)];
  if (matches.length === 0) return null;

  const parts: ReactNode[] = [];
  let cursor = 0;
  matches.forEach((match, index) => {
    const start = match.index ?? 0;
    if (start > cursor) parts.push(raw.slice(cursor, start));
    parts.push(tokenSpan(match[1] ?? '', index, chip));
    cursor = start + match[0].length;
  });
  if (cursor < raw.length) parts.push(raw.slice(cursor));
  return <>{parts}</>;
}

/**
 * A one-line text (a roles responsibility, a subheading, a title) for display: its `{{tokens}}` are
 * resolved, or - Sample data off - left as chips. Plain text without a token is returned as written.
 */
export function resolveDisplayText(text: string, options: { unbound: boolean; resolve: (text: string) => string }): ReactNode {
  if (!options.unbound) return options.resolve(text);
  return renderUnboundTokens(text) ?? options.resolve(text);
}

/**
 * The chips for one field, or undefined when the field renders normally: the export surface, a
 * bound context (sample data or a host quote) and non-text styles (image, logo, prose) never get them.
 */
export function unboundValueDisplay(field: BlockField, context: DocumentContext, exported: boolean): ReactNode | undefined {
  if (exported || context.previewValueBinding !== 'unbound' || !TEXT_STYLES.has(field.style)) return undefined;
  const chip = field.style !== 'title';
  return renderUnboundTokens(field.value, { chip }) ?? undefined;
}
